import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { DisplayValueDescriptor, GaGoalData } from '@vendasta/uikit';
import { differenceInDays, endOfDay, startOfDay } from 'date-fns';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import {
  calcChangeDirection,
  calcMetricTrend,
  convertSecondsToTimeString,
  formatYAxisNumberScale,
  getMonthDayShortName,
  previousTimePeriod,
} from '../../core/metrics.helper';
import { AnalyticsJSON } from '../analytics/analytics.interfaces';
import { DateRangeSelectorService } from '../date-range-selector';
import {
  ChartSeries,
  countTotalFromChartData,
  fillInCurrentTotal,
  fillInDataForEveryDay,
  fillInIsEmptyValue,
} from '../metrics';
import {
  COLLECTION_DAYS_THRESHOLD,
  GA4_ACTIVE_USERS_CHART_CONFIG,
  GA4_AVERAGE_SESSION_CHART_CONFIG,
  GA4_BOUNCE_RATE_CHART_CONFIG,
  GA4_ENGAGED_SESSIONS_CHART_CONFIG,
  GA4_NEW_USERS_CHART_CONFIG,
  GA4_PAGE_VIEWS_CHART_CONFIG,
  GA4_PAGE_VIEWS_PER_SESSION_CHART_CONFIG,
  GA4_SESSIONS_CHART_CONFIG,
  GA_GOALS_CONFIG,
  GA_TOP_REFERRAL_SOURCES_CONFIG,
  GA_TRAFFIC_SOURCES_CONFIG,
} from './ga4-metrics-config';
import { Ga4GoalMap, Ga4RecordMap, Ga4SourceMap, Ga4TableData } from './ga4-metrics.interface';
import { Ga4MetricsService } from './ga4-metrics.service';

@Component({
  selector: 'app-ga4-metrics',
  templateUrl: './ga4-metrics.component.html',
  styleUrls: ['./ga4-metrics.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class Ga4MetricsComponent implements OnInit, OnDestroy {
  @Input() partnerId: string;
  @Input() businessId: string;
  @Input() siteId: string;
  @Input() defaultTracking: boolean;

  legacyDefaultPropertyId: string;
  accessToken: string;
  sessionCalVal: any;
  engSessionCalVal: any;
  activeUsersCurrentValue$: Observable<DisplayValueDescriptor>;
  sessionsCurrentValue$: Observable<DisplayValueDescriptor>;
  sessionDurationCurrentValue$: Observable<DisplayValueDescriptor>;
  bounceRateCurrentValue$: Observable<DisplayValueDescriptor>;
  pageViewsCurrentValue$: Observable<DisplayValueDescriptor>;
  pageViewsPerSessionCurrentValue$: Observable<DisplayValueDescriptor>;
  newUsersCurrentValue$: Observable<DisplayValueDescriptor>;
  enagagedSessionsCurrentValue$: Observable<DisplayValueDescriptor>;

  activeUsersPrevValue$: Observable<DisplayValueDescriptor>;
  sessionsPrevValue$: Observable<DisplayValueDescriptor>;
  engSessionsPrevValue$: Observable<DisplayValueDescriptor>;
  bounceRatePrevValue$: Observable<DisplayValueDescriptor>;
  pageViewsPerSessionPrevValue$: Observable<DisplayValueDescriptor>;
  pageViewsPrevValue$: Observable<DisplayValueDescriptor>;
  avgSessionDurationPrevValue$: Observable<DisplayValueDescriptor>;
  newusersPrevValue$: Observable<DisplayValueDescriptor>;

  sessionsPreviousDataValue$: Observable<DisplayValueDescriptor>;
  engSessionPreviousDataValue$: Observable<DisplayValueDescriptor>;
  bounceRatePreviousDataValue$: Observable<DisplayValueDescriptor>;
  avgSessionDurationPrevDataValue$: Observable<DisplayValueDescriptor>;

  activeUsersIsEmpty$: Observable<boolean>;
  sessionsIsEmpty$: Observable<boolean>;
  sessionDurationIsEmpty$: Observable<boolean>;
  bounceRateIsEmpty$: Observable<boolean>;
  pageViewsIsEmpty$: Observable<boolean>;
  pageViewsPerSessionIsEmpty$: Observable<boolean>;
  newUsersIsEmpty$: Observable<boolean>;
  enagagedSessionsIsEmpty$: Observable<boolean>;
  readonly cardConfig = {
    activeUsers: GA4_ACTIVE_USERS_CHART_CONFIG,
    sessions: GA4_SESSIONS_CHART_CONFIG,
    sessionDuration: GA4_AVERAGE_SESSION_CHART_CONFIG,
    pageViews: GA4_PAGE_VIEWS_CHART_CONFIG,
    pageViewsPerSession: GA4_PAGE_VIEWS_PER_SESSION_CHART_CONFIG,
    newUsers: GA4_NEW_USERS_CHART_CONFIG,
    bounceRate: GA4_BOUNCE_RATE_CHART_CONFIG,
    engagedSessions: GA4_ENGAGED_SESSIONS_CHART_CONFIG,
    trafficSources: GA_TRAFFIC_SOURCES_CONFIG,
    topReferralSources: GA_TOP_REFERRAL_SOURCES_CONFIG,
    goals: GA_GOALS_CONFIG,
  };
  private activeUsersCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- active Users ---
  readonly activeUsersCurrentData$: Observable<ChartSeries[]> = this.activeUsersCurrentData$$.asObservable();
  private sessionsCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- Sessions ---
  readonly sessionsCurrentData$: Observable<ChartSeries[]> = this.sessionsCurrentData$$.asObservable();

  private sessionDurationCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- Sessions Duration---
  readonly sessionDurationCurrentData$: Observable<ChartSeries[]> = this.sessionDurationCurrentData$$.asObservable();

  // --- Page Views ---
  private pageViewsCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  readonly pageViewsCurrentData$: Observable<ChartSeries[]> = this.pageViewsCurrentData$$.asObservable();

  // --- Page Views Per Session---
  private pageViewsPerSessionCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(
    undefined,
  );
  readonly pageViewsPerSessionCurrentData$: Observable<ChartSeries[]> =
    this.pageViewsPerSessionCurrentData$$.asObservable();

  // --- New Users---
  private newUsersCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  readonly newUsersCurrentData$: Observable<ChartSeries[]> = this.newUsersCurrentData$$.asObservable();

  private bounceRateCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- Bounce Rate ---
  readonly bounceRateCurrentData$: Observable<ChartSeries[]> = this.bounceRateCurrentData$$.asObservable();

  private engSessionCurrentData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- Bounce Rate ---
  readonly engSessionCurrentData$: Observable<ChartSeries[]> = this.engSessionCurrentData$$.asObservable();

  private activeUsersCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- active UsersTotal ---
  readonly activeUsersCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.activeUsersCurrentDataTotal$$.asObservable();

  private sessionsCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- Sessions ---
  readonly sessionsCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.sessionsCurrentDataTotal$$.asObservable();

  private sessionDurationCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // ---  Sessions Duration ---
  readonly sessionDurationCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.sessionDurationCurrentDataTotal$$.asObservable();

  // --- Page Views ---
  private pageViewsCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly pageViewsCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.pageViewsCurrentDataTotal$$.asObservable();

  // --- Page Views Per Session ---
  private pageViewsPerSessionCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly pageViewsPerSessionCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.pageViewsPerSessionCurrentDataTotal$$.asObservable();

  // --- New Users ---
  private newUsersCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly newUsersCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.newUsersCurrentDataTotal$$.asObservable();

  // --- Engaged Session ---
  private engSessionCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly engSessionCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.engSessionCurrentDataTotal$$.asObservable();

  private bounceRateCurrentDataTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- Bounce Rate ---
  readonly bounceRateCurrentDataTotal$: Observable<DisplayValueDescriptor> =
    this.bounceRateCurrentDataTotal$$.asObservable();

  private sessionsPrevData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- Sessions ---
  readonly sessionsPrevData$: Observable<ChartSeries[]> = this.sessionsPrevData$$.asObservable();
  private engSessionsPrevData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- Sessions ---
  readonly engSessionsPrevData$: Observable<ChartSeries[]> = this.engSessionsPrevData$$.asObservable();
  private avgSessionDurationPrevData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- Sessions ---
  readonly avgSessionDurationPrevData$: Observable<ChartSeries[]> = this.avgSessionDurationPrevData$$.asObservable();

  private pageViewsPrevData$$: BehaviorSubject<ChartSeries[]> = new BehaviorSubject<ChartSeries[]>(undefined);
  // --- PAGE VIEWS ---
  readonly pageViewsPrevData$: Observable<ChartSeries[]> = this.pageViewsPrevData$$.asObservable();

  private activeUsersPreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- active Users ---
  readonly activeUsersPreviousValue$: Observable<DisplayValueDescriptor> =
    this.activeUsersPreviousValue$$.asObservable();

  private sessionsPreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- Sessions ---
  readonly sessionsPreviousValue$: Observable<DisplayValueDescriptor> = this.sessionsPreviousValue$$.asObservable();

  private sessionDurationPreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // ---  Sessions Duration ---
  readonly sessionDurationPreviousValue$: Observable<DisplayValueDescriptor> =
    this.sessionDurationPreviousValue$$.asObservable();

  // --- Page Views ---
  private pageViewsPreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly pageViewsPreviousValue$: Observable<DisplayValueDescriptor> = this.pageViewsPreviousValue$$.asObservable();

  // --- Page Views Per Session ---
  private pageViewsPerSessionPreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly pageViewsPerSessionPreviousValue$: Observable<DisplayValueDescriptor> =
    this.pageViewsPerSessionPreviousValue$$.asObservable();

  // --- New Users ---
  private newUsersPreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly newUsersPreviousValue$: Observable<DisplayValueDescriptor> = this.newUsersPreviousValue$$.asObservable();

  // --- Engaged Session ---
  private engSessionPreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly engSessionPreviousValue$: Observable<DisplayValueDescriptor> = this.engSessionPreviousValue$$.asObservable();

  private bounceRatePreviousValue$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- Bounce Rate ---
  readonly bounceRatePreviousValue$: Observable<DisplayValueDescriptor> = this.bounceRatePreviousValue$$.asObservable();

  private activeUsersPreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- active Users ---
  readonly activeUsersPreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.activeUsersPreviousValueTotal$$.asObservable();

  private sessionsPreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- Sessions ---
  readonly sessionsPreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.sessionsPreviousValueTotal$$.asObservable();

  private sessionDurationPreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // ---  Sessions Duration ---
  readonly sessionDurationPreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.sessionDurationPreviousValueTotal$$.asObservable();

  // --- Page Views ---
  private pageViewsPreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly pageViewsPreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.pageViewsPreviousValueTotal$$.asObservable();

  // --- Page Views Per Session ---
  private pageViewsPerSessionPreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly pageViewsPerSessionPreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.pageViewsPerSessionPreviousValueTotal$$.asObservable();

  // --- New Users ---
  private newUsersPreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly newUsersPreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.newUsersPreviousValueTotal$$.asObservable();

  // --- Engaged Session ---
  private engSessionPreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  readonly engSessionPreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.engSessionPreviousValueTotal$$.asObservable();

  private bounceRatePreviousValueTotal$$: BehaviorSubject<DisplayValueDescriptor> =
    new BehaviorSubject<DisplayValueDescriptor>(undefined);
  // --- Bounce Rate ---
  readonly bounceRatePreviousValueTotal$: Observable<DisplayValueDescriptor> =
    this.bounceRatePreviousValueTotal$$.asObservable();

  trafficSourceData$$: BehaviorSubject<Ga4TableData[]> = new BehaviorSubject<Ga4TableData[]>(undefined);
  topReferralSourceData$$: BehaviorSubject<Ga4TableData[]> = new BehaviorSubject<Ga4TableData[]>(undefined);
  goals$$: BehaviorSubject<GaGoalData[]> = new BehaviorSubject<GaGoalData[]>(undefined);

  // --- traffic sources ---
  readonly trafficSourceData$: Observable<Ga4TableData[]> = this.trafficSourceData$$.asObservable();
  // --- top referral sources ---
  readonly topReferralSourceData$: Observable<Ga4TableData[]> = this.topReferralSourceData$$.asObservable();
  // --- goals ---
  readonly goals$: Observable<GaGoalData[]> = this.goals$$.asObservable();

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  readonly colorScheme: Color = {
    name: 'ga4-metrics',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#057ec1'],
  };
  readonly schemeType = ScaleType.Ordinal;

  constructor(
    private ga4MetricsService: Ga4MetricsService,
    private dateRangeSelectorService: DateRangeSelectorService,
    private http: HttpClient,
  ) {}

  populateGoals(goalData$$: BehaviorSubject<GaGoalData[]>, goalMap: Ga4GoalMap): void {
    const goalData = this.populateGoalsHelper(goalMap);
    goalData$$.next(goalData);
  }

  populateGoalsHelper(goalMap: Ga4GoalMap): GaGoalData[] {
    const goalData = [];
    const items = Object.keys(goalMap).map((key) => {
      return [goalMap[key].name, goalMap[key].completions];
    });
    items.sort((first: [string, number], second: [string, number]) => {
      return second[1] - first[1];
    });
    items.forEach(([goalName, completions]: [string, number]) => {
      goalData.push({ name: goalName, completions: completions } as GaGoalData);
    });
    return goalData;
  }

  populateTable(
    sourceData$$: BehaviorSubject<Ga4TableData[]>,
    currentSourceMap: Ga4SourceMap,
    prevSourceMap: Ga4SourceMap,
    limit?: number,
  ): void {
    const data = this.populateTableHelper(currentSourceMap, prevSourceMap, limit);
    sourceData$$.next(data);
  }

  populateTableHelper(currentSourceMap: Ga4SourceMap, prevSourceMap: Ga4SourceMap, limit?: number): Ga4TableData[] {
    const tableData = [];
    let currentTotal = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let previousTotal = 0;
    let items = Object.keys(currentSourceMap).map((key) => {
      currentTotal += currentSourceMap[key];
      return [key, currentSourceMap[key]];
    });

    Object.keys(prevSourceMap).forEach((key) => {
      previousTotal += prevSourceMap[key];
    });
    items.sort((first: [string, number], second: [string, number]) => {
      return second[1] - first[1];
    });
    if (!!limit && items.length > limit) {
      items = items.slice(0, limit);
    }
    items.forEach(([source, measureValue]: [string, number]) => {
      const currentPercentForSource = currentTotal ? Number(measureValue) : 0;
      const previousPercentForSource = Number(prevSourceMap[source]);
      const formattedSource = source.replace(/[{()}]/g, '');
      const roundedDiff = previousPercentForSource
        ? Number((((currentPercentForSource - previousPercentForSource) / previousPercentForSource) * 100).toFixed(2))
        : 0;
      tableData.push({
        name: formattedSource,
        value: currentPercentForSource,
        changeDirection: calcChangeDirection(roundedDiff),
        trend: calcMetricTrend(roundedDiff),
        diff: Math.abs(roundedDiff),
      } as Ga4TableData);
    });
    return tableData;
  }

  ngOnInit(): void {
    this.authenticate(this.siteId).subscribe((data) => {
      this.legacyDefaultPropertyId = data.property_id;
      this.accessToken = data.access_token;
      this.dateRangeSelectorService.dateRange$
        .pipe(
          switchMap(([start, end]) => {
            const prevRange = previousTimePeriod(start, end);
            const ga4RecordCombined = combineLatest([
              this.ga4MetricsService.getGa4Records(
                this.partnerId,
                this.businessId,
                startOfDay(start),
                endOfDay(end),
                this.siteId,
                this.defaultTracking,
                this.legacyDefaultPropertyId,
                this.accessToken,
              ),
              this.ga4MetricsService.getGa4Records(
                this.partnerId,
                this.businessId,
                startOfDay(prevRange[0]),
                endOfDay(prevRange[1]),
                this.siteId,
                this.defaultTracking,
                this.legacyDefaultPropertyId,
                this.accessToken,
              ),
            ]);
            const ga4RecordCombinedTotal = combineLatest([
              this.ga4MetricsService.getGa4RecordstTotal(
                this.partnerId,
                this.businessId,
                startOfDay(start),
                endOfDay(end),
                this.siteId,
                this.defaultTracking,
                this.legacyDefaultPropertyId,
                this.accessToken,
              ),
              this.ga4MetricsService.getGa4RecordstTotal(
                this.partnerId,
                this.businessId,
                startOfDay(prevRange[0]),
                endOfDay(prevRange[1]),
                this.siteId,
                this.defaultTracking,
                this.legacyDefaultPropertyId,
                this.accessToken,
              ),
            ]);

            const gaTrafficSourcesCombined = combineLatest([
              this.ga4MetricsService.getTrafficSources(
                this.partnerId,
                this.businessId,
                startOfDay(start),
                endOfDay(end),
                this.siteId,
                this.defaultTracking,
                this.accessToken,
              ),
              this.ga4MetricsService.getTrafficSources(
                this.partnerId,
                this.businessId,
                startOfDay(prevRange[0]),
                endOfDay(prevRange[1]),
                this.siteId,
                this.defaultTracking,
                this.accessToken,
              ),
            ]);

            const gaTopReferralSourcesCombined = combineLatest([
              this.ga4MetricsService.getTopReferralSources(
                this.partnerId,
                this.businessId,
                startOfDay(start),
                endOfDay(end),
                this.siteId,
                this.defaultTracking,
                this.accessToken,
              ),
              this.ga4MetricsService.getTopReferralSources(
                this.partnerId,
                this.businessId,
                startOfDay(prevRange[0]),
                endOfDay(prevRange[1]),
                this.siteId,
                this.defaultTracking,
                this.accessToken,
              ),
            ]);
            const gaGoalsCombined = combineLatest([
              this.ga4MetricsService.getGoals(
                this.partnerId,
                this.businessId,
                startOfDay(start),
                endOfDay(end),
              ),
            ]);

            return combineLatest([
              ga4RecordCombined,
              ga4RecordCombinedTotal,
              gaTrafficSourcesCombined,
              gaTopReferralSourcesCombined,
              gaGoalsCombined,
            ]).pipe(
              map(
                ([
                  [currentGa4RecordMap, previousGa4RecordMap],
                  [currentGa4TotalRecord, previousGa4RecordMapTotal],
                  [currentTrafficSourceMap, previousTrafficSourceMap],
                  [currentTopReferralSourceMap, previousTopReferralSourceMap],
                  [goalsMap],
                ]) => {
                  this.fillInDataForGraph(
                    'activeUsers',
                    this.activeUsersCurrentData$$,
                    currentGa4RecordMap,
                    this.activeUsersCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.fillInDataForGraph(
                    'sessions',
                    this.sessionsCurrentData$$,
                    currentGa4RecordMap,
                    this.sessionsCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.fillInDataForGraph(
                    'sessionDuration',
                    this.sessionDurationCurrentData$$,
                    currentGa4RecordMap,
                    this.sessionDurationCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.fillInDataForGraph(
                    'pageViews',
                    this.pageViewsCurrentData$$,
                    currentGa4RecordMap,
                    this.pageViewsCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.fillInDataForGraph(
                    'pageViewsPerSession',
                    this.pageViewsPerSessionCurrentData$$,
                    currentGa4RecordMap,
                    this.pageViewsPerSessionCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.fillInDataForGraph(
                    'newUsers',
                    this.newUsersCurrentData$$,
                    currentGa4RecordMap,
                    this.newUsersCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.fillInPreviousValueDisplay(
                    'activeUsers',
                    this.activeUsersPreviousValue$$,
                    previousGa4RecordMap,
                    this.activeUsersPreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'total',
                  );
                  this.fillInPreviousValueData('sessions', this.sessionsPrevData$$, previousGa4RecordMap, 'total');
                  this.fillInPreviousValueDisplay(
                    'sessions',
                    this.sessionsPreviousValue$$,
                    previousGa4RecordMap,
                    this.sessionsPreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'total',
                  );
                  this.fillInPreviousValueData(
                    'sessionDuration',
                    this.avgSessionDurationPrevData$$,
                    previousGa4RecordMap,
                    'total',
                  );
                  this.fillInPreviousValueDisplay(
                    'sessionDuration',
                    this.sessionDurationPreviousValue$$,
                    previousGa4RecordMap,
                    this.sessionDurationPreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'total',
                  );
                  this.fillInPreviousValueDisplay(
                    'bounceRate',
                    this.bounceRatePreviousValue$$,
                    previousGa4RecordMap,
                    this.bounceRatePreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'average',
                  );
                  this.fillInPreviousValueDisplay(
                    'pageViews',
                    this.pageViewsPreviousValue$$,
                    previousGa4RecordMap,
                    this.pageViewsPreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'total',
                  );
                  this.fillInPreviousValueDisplay(
                    'engagedSessions',
                    this.engSessionPreviousValue$$,
                    previousGa4RecordMap,
                    this.engSessionPreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'total',
                  );
                  this.fillInPreviousValueData('pageViews', this.pageViewsPrevData$$, previousGa4RecordMap, 'total');
                  this.fillInPreviousValueDisplay(
                    'pageViewsPerSession',
                    this.pageViewsPerSessionPreviousValue$$,
                    previousGa4RecordMap,
                    this.pageViewsPerSessionPreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'average',
                  );
                  this.fillInPreviousValueDisplay(
                    'newUsers',
                    this.newUsersPreviousValue$$,
                    previousGa4RecordMap,
                    this.newUsersPreviousValueTotal$$,
                    previousGa4RecordMapTotal,
                    'total',
                  );
                  this.fillInDataForGraph(
                    'engagedSessions',
                    this.engSessionCurrentData$$,
                    currentGa4RecordMap,
                    this.engSessionCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.fillInPreviousValueData(
                    'engagedSessions',
                    this.engSessionsPrevData$$,
                    previousGa4RecordMap,
                    'total',
                  );
                  this.fillInDataForGraph(
                    'bounceRate',
                    this.bounceRateCurrentData$$,
                    currentGa4RecordMap,
                    this.bounceRateCurrentDataTotal$$,
                    currentGa4TotalRecord,
                    start,
                    end,
                  );
                  this.populateTable(this.trafficSourceData$$, currentTrafficSourceMap, previousTrafficSourceMap, 10);
                  this.populateTable(
                    this.topReferralSourceData$$,
                    currentTopReferralSourceMap,
                    previousTopReferralSourceMap,
                    10,
                  );
                  this.populateGoals(this.goals$$, goalsMap);
                },
              ),
            );
          }),
          takeUntil(this.destroyed$),
        )
        .subscribe();
    });
    if (this.defaultTracking) {
      this.activeUsersCurrentValue$ = this.activeUsersCurrentDataTotal$;
      this.sessionsCurrentValue$ = this.sessionsCurrentDataTotal$;
      this.sessionDurationCurrentValue$ = this.sessionDurationCurrentDataTotal$;
      this.enagagedSessionsCurrentValue$ = this.engSessionCurrentDataTotal$;
      this.bounceRateCurrentValue$ = this.bounceRateCurrentDataTotal$;
      this.pageViewsCurrentValue$ = this.pageViewsCurrentDataTotal$;
      this.pageViewsPerSessionCurrentValue$ = this.pageViewsPerSessionCurrentDataTotal$;
      this.newUsersCurrentValue$ = this.newUsersCurrentDataTotal$;

      this.activeUsersPrevValue$ = this.activeUsersPreviousValueTotal$;
      this.sessionsPrevValue$ = this.sessionsPreviousValueTotal$;
      this.avgSessionDurationPrevValue$ = this.sessionDurationPreviousValueTotal$;
      this.pageViewsPrevValue$ = this.pageViewsPreviousValueTotal$;
      this.pageViewsPerSessionPrevValue$ = this.pageViewsPerSessionPreviousValueTotal$;
      this.newusersPrevValue$ = this.newUsersPreviousValueTotal$;
      this.bounceRatePrevValue$ = this.bounceRatePreviousValueTotal$;
      this.engSessionsPrevValue$ = this.engSessionPreviousValueTotal$;
    } else {
      this.activeUsersCurrentValue$ = fillInCurrentTotal(this.activeUsersCurrentData$);
      this.sessionsCurrentValue$ = fillInCurrentTotal(this.sessionsCurrentData$);
      this.sessionDurationCurrentValue$ = fillInCurrentTotal(this.sessionDurationCurrentData$);
      this.sessionDurationCurrentValue$ = this.fillInBounceRateCal(
        this.sessionDurationCurrentValue$,
        this.sessionsCurrentValue$,
        'avgSessionDuration',
      );
      this.enagagedSessionsCurrentValue$ = fillInCurrentTotal(this.engSessionCurrentData$);
      this.bounceRateCurrentValue$ = this.fillInBounceRateCal(
        this.enagagedSessionsCurrentValue$,
        this.sessionsCurrentValue$,
        'bounceRate',
      );
      this.pageViewsCurrentValue$ = fillInCurrentTotal(this.pageViewsCurrentData$);
      this.pageViewsPerSessionCurrentValue$ = this.fillInBounceRateCal(
        this.pageViewsCurrentValue$,
        this.sessionsCurrentValue$,
        'pageViewsPerSession',
      );
      this.newUsersCurrentValue$ = fillInCurrentTotal(this.newUsersCurrentData$);

      this.activeUsersPrevValue$ = this.activeUsersPreviousValue$;
      this.sessionsPrevValue$ = fillInCurrentTotal(this.sessionsPrevData$);
      this.engSessionsPrevValue$ = fillInCurrentTotal(this.engSessionsPrevData$);
      this.bounceRatePrevValue$ = this.fillInBounceRateCal(
        this.engSessionsPrevValue$,
        this.sessionsPrevValue$,
        'bounceRate',
      );
      this.pageViewsPrevValue$ = fillInCurrentTotal(this.pageViewsPrevData$);
      this.pageViewsPerSessionPrevValue$ = this.fillInBounceRateCal(
        this.pageViewsPrevValue$,
        this.sessionsPrevValue$,
        'pageViewsPerSession',
      );
      this.avgSessionDurationPrevDataValue$ = fillInCurrentTotal(this.avgSessionDurationPrevData$);
      this.avgSessionDurationPrevValue$ = this.fillInBounceRateCal(
        this.avgSessionDurationPrevDataValue$,
        this.sessionsPrevValue$,
        'avgSessionDuration',
      );
      this.newusersPrevValue$ = this.newUsersPreviousValue$;
    }

    this.activeUsersIsEmpty$ = fillInIsEmptyValue(this.activeUsersCurrentData$);
    this.sessionsIsEmpty$ = fillInIsEmptyValue(this.sessionsCurrentData$);
    this.sessionDurationIsEmpty$ = fillInIsEmptyValue(this.sessionDurationCurrentData$);
    this.enagagedSessionsIsEmpty$ = fillInIsEmptyValue(this.engSessionCurrentData$);
    this.bounceRateIsEmpty$ = fillInIsEmptyValue(this.bounceRateCurrentData$);
    this.pageViewsIsEmpty$ = fillInIsEmptyValue(this.pageViewsCurrentData$);
    this.pageViewsPerSessionIsEmpty$ = fillInIsEmptyValue(this.pageViewsPerSessionCurrentData$);
    this.newUsersIsEmpty$ = fillInIsEmptyValue(this.newUsersCurrentData$);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  fillInBounceRateCal(
    commonData$: Observable<DisplayValueDescriptor>,
    sessionData$: Observable<DisplayValueDescriptor>,
    metric: string,
  ): Observable<DisplayValueDescriptor> {
    return combineLatest([commonData$, sessionData$]).pipe(
      map(([commonData, sessionData]) => {
        if (metric === 'bounceRate') {
          this.sessionCalVal =
            ((Number(sessionData.value) - Number(commonData.value)) / Number(sessionData.value)) * 100;
        }
        if (metric === 'pageViewsPerSession') {
          this.sessionCalVal = Number(commonData.value) / Number(sessionData.value);
        }
        if (metric === 'avgSessionDuration') {
          this.sessionCalVal = Number(commonData.value) / Number(sessionData.value);
        }
        if (Number.isNaN(this.sessionCalVal)) {
          this.sessionCalVal = 0;
        }
        return {
          value: this.sessionCalVal,
          displayType: 'number',
        } as DisplayValueDescriptor;
      }),
    );
  }

  fillInDataForGraph(
    graphName: string,
    data$$: BehaviorSubject<ChartSeries[]>,
    currentGa4RecordMap: Ga4RecordMap,
    dataTotal$$: BehaviorSubject<DisplayValueDescriptor>,
    currentGa4TotalRecord: Ga4RecordMap,
    start: Date,
    end: Date,
  ): void {
    let chartData = currentGa4RecordMap[graphName];
    if (graphName in currentGa4RecordMap) {
      if (differenceInDays(end, start) < COLLECTION_DAYS_THRESHOLD) {
        chartData = fillInDataForEveryDay(currentGa4RecordMap[graphName], start, end);
      }
      data$$.next([
        {
          name: this.cardConfig[graphName].header.title,
          series: chartData,
        } as ChartSeries,
      ]);
    } else {
      data$$.next([]);
    }
    if (graphName in currentGa4TotalRecord) {
      dataTotal$$.next({
        value: currentGa4TotalRecord[graphName][0].value,
        displayType: 'number',
      } as DisplayValueDescriptor);
    } else {
      dataTotal$$.next({
        value: 0,
        displayType: 'number',
      } as DisplayValueDescriptor);
    }
  }

  fillInPreviousValueData(
    graphName: string,
    previousValue$$: BehaviorSubject<ChartSeries[]>,
    previousGa4RecordMap: Ga4RecordMap,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    calcType?: string,
  ): void {
    if (graphName in previousGa4RecordMap) {
      const chartData = previousGa4RecordMap[graphName];

      previousValue$$.next([
        {
          name: this.cardConfig[graphName].header.title,
          series: chartData,
        } as ChartSeries,
      ]);
    } else {
      previousValue$$.next([]);
    }
  }

  fillInPreviousValueDisplay(
    graphName: string,
    previousValue$$: BehaviorSubject<DisplayValueDescriptor>,
    previousGa4RecordMap: Ga4RecordMap,
    previousValueTotal$$: BehaviorSubject<DisplayValueDescriptor>,
    previousGa4RecordMapTotal: Ga4RecordMap,
    calcType?: string,
  ): void {
    if (graphName in previousGa4RecordMap) {
      const chartData = previousGa4RecordMap[graphName];
      let v = countTotalFromChartData([
        {
          name: graphName,
          series: chartData,
        } as ChartSeries,
      ]);
      if (calcType === 'average') {
        v = v / chartData.length;
      }
      previousValue$$.next({
        value: v.toFixed(2),
        displayType: 'number',
      } as DisplayValueDescriptor);
    } else {
      previousValue$$.next({
        value: 0,
        displayType: 'number',
      } as DisplayValueDescriptor);
    }
    if (graphName in previousGa4RecordMapTotal) {
      previousValueTotal$$.next({
        value: previousGa4RecordMapTotal[graphName][0].value,
        displayType: 'number',
      } as DisplayValueDescriptor);
    } else {
      previousValueTotal$$.next({
        value: 0,
        displayType: 'number',
      } as DisplayValueDescriptor);
    }
  }

  formatBounceRate(r: DisplayValueDescriptor): DisplayValueDescriptor {
    r.value = r.value + '%';
    r.displayType = 'string';
    return r;
  }

  formatMonthString(date: Date): string {
    return getMonthDayShortName(date);
  }

  formatYAxisScale(value: number): string {
    return formatYAxisNumberScale(value);
  }

  convertSecondsToHHMMSS(sec: DisplayValueDescriptor): DisplayValueDescriptor {
    sec.value = convertSecondsToTimeString(sec.value as number);
    sec.displayType = 'string';
    return sec;
  }

  convertGraphSecondsToHHMMSS(sec: number): string {
    const seconds = convertSecondsToTimeString(sec);
    return seconds;
  }

  /**
   * Authenticates with the legacy Google Analytics connection by getting authentication
   * data from the backend
   * @param siteId
   */
  authenticate(siteId: string): Observable<AnalyticsJSON> {
    const url = `/api/v1/analytics/${siteId}/authenticate`;
    return this.http.get(url).pipe(map((res: { data: any }) => res.data));
  }
}
